import {FC, useCallback, useEffect, useState} from 'react';
import {Alert, Badge, Button, ButtonLink, ButtonSizes, ButtonStyles, Checkbox, Icon, Icons, showAlert} from '@silverfort/sf-components';
import EulaPopup from '../EulaPopup/EulaPopup';
import LicensePopup from '../License/LicensePopup';
import {LICENSE_FIELDS} from '../../common/fields';
import LicenseModulesTable from './LicenseModulesTable';
import * as actions from '../../containers/Licensing/licensingActions';
import moment from 'moment';
import {httpGet, httpPost} from '../../../utils';
import {usePricingModulesState} from '../../../../context/contexts/PricingModuleContext';
import useFeature from '../../hooks/featureFlags/useFeature';
import {FEATURE_FLAGS_NAMES} from '@silverfort-nodelibs/common/utils/consts';
import {SYSTEM_MODES} from '@silverfort-nodelibs/common/utils/system-mode-config';

const DAY_IN_MS = 1000 * 60 * 60 * 24;

const License: FC<any> = ({fields, dispatch, save, onCancel, licenseExtraData, minified = false}) => {
    const [showEula, setShowEula] = useState(false);
    const {fetchPricingModulesState} = usePricingModulesState();
    const systemMode = useFeature(FEATURE_FLAGS_NAMES.SystemMode);
    const isSaasMode = systemMode === SYSTEM_MODES.SAAS;
    const [showEditLicenseKey, setShowEditLicenseKey] = useState(false);
    const [showPurchasedOnly, setShowPurchasedOnly] = useState(minified);
    const [pricingModulesDisplay, setPricingModulesDisplay] = useState<any[]>([]);
    const [licenseLastUpdated, setLicenseLastUpdated] = useState(null);
    const [licenseLastSynced, setLicenseLastSynced] = useState(null);
    const [licenseExpiration, setLicenseExpiration] = useState<number | null>(null);
    const [isGeneratingLicense, setIsGeneratingLicense] = useState(true);
    const [isSyncing, setIsSyncing] = useState(false);
    const license = fields[LICENSE_FIELDS.LICENSE_KEY.ID];

    const onLicenseUpdate = useCallback(
        (licenseDetails, senderConnectionStrings, receiverConnectionStrings) => {
            dispatch({
                type: actions.UPDATE_LICENSE,
                payload: {licenseDetails, senderConnectionStrings, receiverConnectionStrings}
            });
        },
        [dispatch]
    );

    const fetchPricingModulesDisplay = useCallback(async () => {
        setIsGeneratingLicense(true);
        try {
            const response = await httpGet('/license/getPricingModulesDisplay');
            const responseData = response.data as any;
            setPricingModulesDisplay(responseData?.modules || []);
            setLicenseLastSynced(responseData?.licenseLastSyncTime || null);
            setLicenseLastUpdated(responseData?.licenseLastUpdateTime || null);
            setLicenseExpiration(responseData?.licenseExpirationDate || null);
        } catch (error) {
            console.error('Failed to fetch pricing modules display:', error);
            showAlert(
                "Failed to sync license. This may happen if the environment is offline or if the license hasn't been activated yet.",
                'warning'
            );
        } finally {
            setIsGeneratingLicense(false);
        }
    }, []);

    const onLicenseSave = useCallback(async () => {
        try {
            await save();
            setShowEditLicenseKey(false);
            fetchPricingModulesDisplay();
            await httpPost('SyncLicense', {});
            await fetchPricingModulesState();
            return {success: true};
        } catch (e: any) {
            console.error(e);
            return {
                errorMessage:
                    e.status === 408 || e.status === 404
                        ? 'Server connection error, failed to activate license'
                        : 'Invalid activation key'
            };
        }
    }, [save, fetchPricingModulesDisplay, fetchPricingModulesState]);

    const onLicenseSync = async () => {
        setIsSyncing(true);
        setIsGeneratingLicense(true);
        try {
            await httpPost('SyncLicense', {});
            await fetchPricingModulesState();
        } catch (e: any) {
            console.error(e);
            return {
                errorMessage:
                    e.status === 408 || e.status === 404
                        ? 'Server connection error, failed to sync license'
                        : 'Invalid activation key'
            };
        } finally {
            await fetchPricingModulesDisplay();
            setIsSyncing(false);
        }
    };

    useEffect(() => {
        fetchPricingModulesDisplay();
    }, [fetchPricingModulesDisplay]);

    const getLicenseExpirationDaysLeft = () => {
        if (!licenseExpiration) return 0;

        const now = Date.now();
        const daysLeft = Math.floor((licenseExpiration - now) / DAY_IN_MS);

        return daysLeft;
    };

    const hasLicenseExpired = getLicenseExpirationDaysLeft() < 0;

    const getLicenseExpirationBadge = () => {
        if (hasLicenseExpired)
            return (
                <Badge
                    className="font-medium"
                    color="secondary"
                    text={'Expired ' + moment(licenseExpiration).format('MMM DD, YYYY')}
                />
            );

        if (getLicenseExpirationDaysLeft() < 30)
            return (
                <Badge
                    icon="Warning"
                    className="font-medium"
                    color="neutral"
                    text={'About to Expire ' + moment(licenseExpiration).format('MMM DD, YYYY')}
                />
            );

        return (
            <Badge className="font-medium" color="primary" text={moment(licenseExpiration).format('MMM DD, YYYY')}/>
        );
    };

    return (
        <>
            <div className="flex flex-col gap-4 font-sf">
                <div className={`flex ${minified ? 'justify-end' : 'justify-between'}`}>
                    {!minified && (
                        <div className="flex items-center gap-2 text-sf-gray-500 text-12">
                            <div className="flex items-center gap-1">
                                <Icon icon={Icons.Refresh} size={16}/>
                                <div className="font-normal">Last update:</div>
                                {!hasLicenseExpired && licenseLastUpdated ? (
                                    <div className="font-medium">
                                        {moment(licenseLastUpdated * 1000).format('MMM DD, YYYY')}
                                    </div>
                                ) : (
                                    <div className="font-medium">-</div>
                                )}
                            </div>
                            <div>•</div>
                            <div className="flex items-center gap-1">
                                <div className="font-normal">Last sync:</div>
                                {!hasLicenseExpired && licenseLastSynced ? (
                                    <div className="font-medium">
                                        {moment(licenseLastSynced * 1000).format('MMM DD, YYYY | hh:mm A')}
                                    </div>
                                ) : (
                                    <div className="font-medium">-</div>
                                )}
                            </div>
                        </div>
                    )}
                    <div className="flex font-sf items-center gap-2">
                        <div className="text-12 text-sf-gray-500">License expiration:</div>
                        {isGeneratingLicense ? (
                            <Badge text="Generating license" color="positive" icon="Sync"/>
                        ) : licenseExpiration ? (
                            getLicenseExpirationBadge()
                        ) : (
                            <div>-</div>
                        )}
                    </div>
                </div>
                <div className="flex justify-between items-center">
                    <Checkbox
                        checked={showPurchasedOnly}
                        onClick={() => setShowPurchasedOnly((prev) => !prev)}
                        disabled={isGeneratingLicense || !pricingModulesDisplay || hasLicenseExpired}
                        label="Show purchased modules only"
                        className="text-13 text-sf-gray-800"
                    />
                    <div className="flex gap-4">
                        <ButtonLink
                            text="View EULA"
                            underline
                            className="text-13 font-normal text-gray-400"
                            onClick={() => setShowEula(true)}
                        />
                        {!isSaasMode && (
                            <>
                                <Button
                                    text="License Key"
                                    iconLeft={Icons.Edit}
                                    buttonStyle={ButtonStyles.Tertiary}
                                    buttonSize={ButtonSizes.Medium}
                                    onClick={() => setShowEditLicenseKey(true)}
                                />
                                {!minified && (
                                    <Button
                                        text={isSyncing ? 'Syncing...' : 'Sync License'}
                                        iconLeft={Icons.Sync}
                                        buttonStyle={ButtonStyles.Tertiary}
                                        buttonSize={ButtonSizes.Medium}
                                        onClick={onLicenseSync}
                                        disabled={isGeneratingLicense || isSyncing}
                                    />
                                )}
                            </>
                        )}
                    </div>
                </div>
                {hasLicenseExpired ? (
                    <div className="flex items-center justify-center h-44 border rounded-lg border-sf-gray-100">
                        <div className="flex flex-col items-center">
                            <div className="text-16 text-sf-gray-800 font-medium">License has expired</div>
                            <div className="text-13 text-sf-gray-500 font-normal">
                                {isSaasMode ? 'Please contact Silverfort support' : 'Update the license key'}
                            </div>
                        </div>
                    </div>
                ) : (
                    <LicenseModulesTable
                        pricingModulesDisplay={pricingModulesDisplay}
                        showEnabledOnly={showPurchasedOnly}
                        licenseExtraData={licenseExtraData}
                    />
                )}
            </div>
            {showEditLicenseKey && (
                <LicensePopup
                    licenseKey={license?.value}
                    onSave={onLicenseSave}
                    onLicenseUpdate={onLicenseUpdate}
                    onCancel={() => {
                        setShowEditLicenseKey(false);
                        onCancel();
                    }}
                />
            )}
            {showEula && (
                <EulaPopup
                    readOnly={true}
                    onDisagree={() => setShowEula(false)}
                    onAgree={() => {
                    }}
                    logoutOnDisagree={false}
                />
            )}
            <Alert/>
        </>
    );
};

export default License;
